import React, { useState, useEffect } from "react";
import { Grid, Paper } from "@material-ui/core";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import ProgressBar from "../progress-bar";
import StepInitial from "./StepInitial";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { injectIntl } from "react-intl";
import Content from "../content";
import Loader from "../loader";
import messages from "./PageEditPlayer.intl";
import { ActionLink } from "../atoms";
import useIsManager from "../../hooks/useIsManager";
import { POSITION_LABELS } from "../../utils/positions";

const QUERY_PLAYER = gql`
  query playerDetails($id: ID!) {
    player(id: $id) {
      id
      name
      surname
      nickName
      number
      position
      dateOfBirth
      team {
        id
        name
      }
      notes {
        note
        date
      }
      totalGoals
      totalMatches
      reviews {
        data {
          speed
          vision
          dribbling
          shoot
          physical
          leadership
          defense
        }
        date
      }
      user {
        id
        email
      }
    }
    currentUser {
      id
      role
    }
  }
`;

export const EDIT_PLAYER = gql`
  mutation EditPlayer(
    $id: ID!
    $name: String!
    $surname: String!
    $number: Int
    $position: String!
    $dateOfBirth: String!
  ) {
    editPlayer(
      id: $id
      input: {
        name: $name
        surname: $surname
        number: $number
        position: $position
        dateOfBirth: $dateOfBirth
      }
    ) {
      id
      name
      surname
      number
      position
      dateOfBirth
    }
  }
`;

const StyledPaper = styled(Paper)(({ theme }: IStyledArguments) => ({
  boxSizing: "border-box",
  padding: theme.spacing(4),
  marginBottom: theme.spacing(5),
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  margin: "0 auto",
  maxWidth: 630,
  [`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
    boxShadow: "none",
  },
}));

const StatusCard = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 20,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  overflow: "hidden",
  maxWidth: 330,
  margin: "0 auto",
  textAlign: "left",
}));

const StatusHeader = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(4, 3.5, 3),
  textAlign: "center",
}));

const AvatarWrap = styled("div")({
  position: "relative",
  width: 76,
  height: 76,
  margin: "0 auto",
});

const AvatarCircle = styled("div")(({ theme }: IStyledArguments) => ({
  width: 76,
  height: 76,
  borderRadius: "50%",
  background: theme.palette.extra.accentSoft,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "1.5rem",
  color: theme.palette.primary.dark,
}));

const CheckBadge = styled("div")(({ theme }: IStyledArguments) => ({
  position: "absolute",
  right: -4,
  bottom: -4,
  width: 28,
  height: 28,
  borderRadius: "50%",
  background: theme.palette.extra.win,
  border: `3px solid ${theme.palette.background.paper}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    color: theme.palette.background.paper,
  },
}));

const StatusTitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "1.15rem",
  color: theme.palette.text.primary,
  marginTop: theme.spacing(2),
}));

const StatusSubtitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.78rem",
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
}));

const Seam = styled("div")(({ theme }: IStyledArguments) => ({
  borderTop: `1px dashed ${theme.palette.extra.borderColor}`,
  margin: theme.spacing(0, 3),
}));

const SummaryList = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(2.5, 3),
}));

const SummaryRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(1, 0),
  fontSize: "0.78rem",
  "& span:first-of-type": {
    color: theme.palette.text.secondary,
  },
  "& span:last-of-type": {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
}));

const StatusFooter = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(0, 3, 3),
}));

const StatusButton = styled("div")<{ $variant?: "primary" | "outlined" }>(
  ({ theme, $variant }: IStyledArguments & { $variant?: "primary" | "outlined" }) => ({
    width: "100%",
    boxSizing: "border-box",
    border: $variant === "outlined" ? `1px solid ${theme.palette.extra.borderColor}` : "none",
    background: $variant === "outlined" ? "transparent" : theme.palette.primary.main,
    color: $variant === "outlined" ? theme.palette.text.primary : theme.palette.primary.contrastText,
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: 600,
    fontSize: "0.85rem",
    borderRadius: 10,
    padding: theme.spacing(1.4, 0),
    cursor: "pointer",
    textDecoration: "none",
    display: "block",
    textAlign: "center",
  })
);

const getInitials = (name: string, surname: string) =>
  `${(name && name[0]) || ""}${(surname && surname[0]) || ""}`.toUpperCase();

const PageEditPlayer = injectIntl<{ id: string }>(({ id, intl }) => {
  const { data, loading } = useQuery<any>(QUERY_PLAYER, {
    variables: {
      id,
    },
  });
  const [addPlayer, { data: editData, loading: loadingData }] = useMutation(EDIT_PLAYER);
  const [currentStep, setCurrentStep] = useState(1);
  const handleEditPlayer = async (form: any) => {
    addPlayer({
      variables: {
        id,
        name: form.name,
        surname: form.surname,
        number: Number(form.number),
        position: form.position,
        dateOfBirth: form.dateOfBirth,
      },
    });
  };

  useEffect(() => {
    if (data) {
      if (!useIsManager(data.currentUser.role) && data.currentUser.id !== data.player.user.id) {
        setCurrentStep(3);
      }
    }
  }, [data]);

  useEffect(() => {
    if (loadingData) {
      return;
    }

    if (editData) {
      setCurrentStep(2);
    }
  }, [editData]);

  return (
    <>
      <Loader errorMessage={intl.formatMessage(messages.error)} loading={loading} data={data}>
        {({ player }) => (
          <>
            <Content>
              <Grid container spacing={0} justify="center" alignItems="center">
                <Grid item xs={12} md={6}>
                  <StyledPaper>
                    <ProgressBar steps={2} activeStep={currentStep} />
                    {currentStep === 1 && (
                      <StepInitial
                        player={player}
                        onGoNext={(values: any) => {
                          handleEditPlayer(values);
                        }}
                      />
                    )}
                    {currentStep === 2 && (
                      <StatusCard>
                        <StatusHeader>
                          <AvatarWrap>
                            <AvatarCircle>{getInitials(player.name, player.surname)}</AvatarCircle>
                            <CheckBadge>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                            </CheckBadge>
                          </AvatarWrap>
                          <StatusTitle>
                            {player.name} {player.surname} updated
                          </StatusTitle>
                          <StatusSubtitle>Saved just now</StatusSubtitle>
                        </StatusHeader>

                        {editData && editData.editPlayer && (
                          <>
                            <Seam />
                            <SummaryList>
                              {editData.editPlayer.number && (
                                <SummaryRow>
                                  <span>Number</span>
                                  <span>#{editData.editPlayer.number}</span>
                                </SummaryRow>
                              )}
                              {editData.editPlayer.position && (
                                <SummaryRow>
                                  <span>Position</span>
                                  <span>{POSITION_LABELS[editData.editPlayer.position] || editData.editPlayer.position}</span>
                                </SummaryRow>
                              )}
                              {editData.editPlayer.dateOfBirth && (
                                <SummaryRow>
                                  <span>Date of birth</span>
                                  <span>{editData.editPlayer.dateOfBirth}</span>
                                </SummaryRow>
                              )}
                            </SummaryList>
                          </>
                        )}

                        <StatusFooter>
                          <ActionLink to="/people/teams" style={{ textDecoration: "none" }}>
                            <StatusButton>Back to team</StatusButton>
                          </ActionLink>
                        </StatusFooter>
                      </StatusCard>
                    )}
                    {currentStep === 3 && (
                      <StatusCard>
                        <StatusHeader>
                          <AvatarWrap style={{ width: 72, height: 72 }}>
                            <AvatarCircle
                              style={{
                                width: 72,
                                height: 72,
                                background: "rgba(185, 68, 56, 0.1)",
                              }}
                            >
                              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#B94438" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 8v5M12 16h.01" />
                              </svg>
                            </AvatarCircle>
                          </AvatarWrap>
                          <StatusTitle>Can't edit this player</StatusTitle>
                          <StatusSubtitle>You can only edit your own details.</StatusSubtitle>
                        </StatusHeader>
                        <StatusFooter>
                          <ActionLink to="/people/teams" style={{ textDecoration: "none" }}>
                            <StatusButton $variant="outlined">Back</StatusButton>
                          </ActionLink>
                        </StatusFooter>
                      </StatusCard>
                    )}
                  </StyledPaper>
                </Grid>
              </Grid>
            </Content>
          </>
        )}
      </Loader>
    </>
  );
});

export default PageEditPlayer;
